import React, { useEffect, useState } from 'react'
import { Box, Dialog, DialogTitle } from '@material-ui/core'
import { connect } from 'react-redux'
import { Form, Formik } from 'formik'
import { addDays, format } from 'date-fns'
import { CardElement } from '@stripe/react-stripe-js'
import { isNil } from 'lodash'

import ButtonDialogClose from '../common/ButtonDialogClose'
import {
  loadProgramUsageTypesStart,
  setCurrentStepStart,
} from '../../../redux/modules/teamPrograms'
import { selectProgramUsageTypes } from '../../../redux/selectors/teamProgramsSelectors'
import { enqueueSnackbar } from '../../../redux/modules/snackbar'
import { selectTeamMembers } from '../../../redux/selectors/teamMembersSelectors'
import { loadTeamMembersStart } from '../../../redux/modules/teamMembers'
import useAddPaymentMethod from '../../../hooks/useAddPaymentMethod'
import { loadTeamPaymentMethodsStart } from '../../../redux/modules/team'
import { getStepDetails, getTotalDottedSteps, STEPS } from './programUtils'
import StepProgramUsageType from './StepProgramUsageType'
import DottedProgress from './DottedProgress'
import StepProgramAmount from './StepProgramAmount'
import StepProgramMembers from './StepProgramMembers'
import StepProgramDetails from './StepProgramDetails'
import StepAddPaymentMethod from './StepAddPaymentMethod'
import ExitConfirmation from './ExitConfirmation'

const DialogCreateProgram = ({
  loadProgramUsageTypesStart,
  isLoadingProgramUsageTypes,
  loadTeamMembersStart,
  isLoadingTeamMembers,
  programUsageTypes,
  teamMembers,
  team,
  defaultPaymentMethod,
  setCurrentStepStart,
  currentStep,
  loadTeamPaymentMethodsStart,
  onClose,
  isCreatingProgram,
  errorCreatingProgram,
  isAddingProgramMembers,
  errorAddingProgramMembers,
}) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)
  const hasDefaultPaymentMethod = !isNil(defaultPaymentMethod)
  const totalSteps = getTotalDottedSteps(hasDefaultPaymentMethod, team.paymentCollectionMethod)
  const confirmedTeamMembers = teamMembers.filter(member => member.status === 'CONFIRMED')
  const { isCreatingPaymentMethod, handleAddPaymentMethod } = useAddPaymentMethod({
    ownerType: 'ENTERPRISE',
    referenceId: team.id,
    onSuccess: () => {
      setTimeout(() => loadTeamPaymentMethodsStart(team.id), 3000)
    },
  })

  const { step, title, nextStep, previousStep, validationSchema } = getStepDetails(
    currentStep,
    hasDefaultPaymentMethod,
    team.paymentCollectionMethod,
  )

  const handleSubmitStep = values => {
    if (currentStep === STEPS.PROGRAM_DETAILS && nextStep === STEPS.PROGRAM_COMPLETE) {
      handleCreateProgram(values)
      return
    }

    if (currentStep === STEPS.PROGRAM_ADD_PAYMENT) {
      handleAddPaymentMethod().then(() => setTimeout(() => handleCreateProgram(values), 3000))
      return
    }

    setCurrentStepStart(nextStep)
  }

  const handleBack = () => {
    setCurrentStepStart(previousStep)
  }

  const handleCreateProgram = values => {
    const programRequest = {
      name: values.name,
      displayName: `${team.name} Subsidy`,
      discountAmountInPennies: values.discountAmountInPennies,
      startDate: values.startDate,
      endDate: values.endDateRequested ? addDays(values.endDate, 1) : null,
      schedule: {
        sundayUsage: values.schedule.sundayUsage,
        mondayUsage: values.schedule.mondayUsage,
        tuesdayUsage: values.schedule.tuesdayUsage,
        wednesdayUsage: values.schedule.wednesdayUsage,
        thursdayUsage: values.schedule.thursdayUsage,
        fridayUsage: values.schedule.fridayUsage,
        saturdayUsage: values.schedule.saturdayUsage,
      },
      usageType: values.usageType,
    }
    const programMembers = values.members

    setCurrentStepStart(
      nextStep,
      {
        createProgramRequest: programRequest,
        programMembers: programMembers,
      },
      onClose,
    )
  }

  const handleClose = () => {
    setShowExitConfirmation(true)
  }

  const onBackFromExit = () => {
    setShowExitConfirmation(false)
  }

  const handleConfirmClose = () => {
    onClose()
  }

  useEffect(() => {
    if (team) {
      loadTeamMembersStart(team.id)
      loadProgramUsageTypesStart()
      setCurrentStepStart(STEPS.PROGRAM_USAGE_TYPE)
    }
  }, [loadProgramUsageTypesStart, loadTeamMembersStart, setCurrentStepStart, team])

  return (
    <Dialog open fullWidth maxWidth="sm">
      <Formik
        initialValues={{
          name: '',
          discountAmountInPennies: 0,
          startDate: format(new Date(), 'YYYY-MM-DD'),
          endDateRequested: false,
          endDate: '',
          schedule: {
            sundayUsage: false,
            mondayUsage: true,
            tuesdayUsage: true,
            wednesdayUsage: true,
            thursdayUsage: true,
            fridayUsage: true,
            saturdayUsage: false,
          },
          usageType: '',
          members: [],
        }}
        onSubmit={handleSubmitStep}
        validationSchema={validationSchema}
      >
        {props => (
          <Form>
            {showExitConfirmation && (
              <ExitConfirmation onBack={onBackFromExit} onConfirm={handleConfirmClose} />
            )}

            {!showExitConfirmation && (
              <>
                <Box display="flex" justifyContent="center" marginTop={3}>
                  <DottedProgress currentStep={step} totalSteps={totalSteps} />
                </Box>
                <DialogTitle>
                  {title}
                  <ButtonDialogClose onClick={handleClose} />
                </DialogTitle>
                {currentStep === STEPS.PROGRAM_USAGE_TYPE && (
                  <StepProgramUsageType
                    programUsageTypes={programUsageTypes}
                    loading={isLoadingProgramUsageTypes}
                    handleSubmitStep={handleSubmitStep}
                    {...props}
                  />
                )}
                {currentStep === STEPS.PROGRAM_AMOUNT && (
                  <StepProgramAmount onBack={handleBack} {...props} />
                )}
                {currentStep === STEPS.PROGRAM_MEMBERS && (
                  <StepProgramMembers
                    teamMembers={confirmedTeamMembers}
                    loading={isLoadingTeamMembers}
                    onBack={handleBack}
                    {...props}
                  />
                )}
                {currentStep === STEPS.PROGRAM_DETAILS && (
                  <StepProgramDetails
                    isPaymentMethodRequired={nextStep === STEPS.PROGRAM_ADD_PAYMENT}
                    onBack={handleBack}
                    submitting={isCreatingProgram || isAddingProgramMembers}
                    error={
                      // only show the error from api-accounts if this is the final step
                      nextStep === STEPS.PROGRAM_COMPLETE
                        ? errorCreatingProgram || errorAddingProgramMembers
                        : undefined
                    }
                    {...props}
                  />
                )}
                {currentStep === STEPS.PROGRAM_ADD_PAYMENT && (
                  <StepAddPaymentMethod
                    team={team}
                    CardElement={CardElement}
                    helperText="All Foodsby perks are charged on the first of each month."
                    onBack={handleBack}
                    submitting={
                      isCreatingPaymentMethod || isCreatingProgram || isAddingProgramMembers
                    }
                    error={errorCreatingProgram || errorAddingProgramMembers}
                    {...props}
                  />
                )}
              </>
            )}
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

const mapStateToProps = state => {
  const {
    currentStep,
    isCreatingProgram,
    errorCreatingProgram,
    isAddingProgramMembers,
    errorAddingProgramMembers,
  } = state.teamPrograms
  return {
    isLoadingProgramUsageTypes: state.teamPrograms.isLoadingProgramUsageTypes,
    programUsageTypes: selectProgramUsageTypes(state),
    currentStep,
    isLoadingTeamMembers: state.teamMembers.isLoadingTeamMembers,
    teamMembers: selectTeamMembers(state),
    isCreatingProgram,
    errorCreatingProgram,
    isAddingProgramMembers,
    errorAddingProgramMembers,
  }
}

const mapDispatchToProps = {
  setCurrentStepStart,
  loadProgramUsageTypesStart,
  loadTeamMembersStart,
  loadTeamPaymentMethodsStart,
  enqueueSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(DialogCreateProgram)
